import { useCallback, useEffect, useMemo } from 'react';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { eLocalStorageKeys } from 'src/constants/AppConstants';
import { DEFAULT_PREFERENCES, FontFamily, HomepageCardVisibility, resolveFontStack, UserPreferences } from './user-preferences.types';

export const useUserPreferences = () => {
  const [storedPreferences, setPreferences] = usePersistedState<UserPreferences>(eLocalStorageKeys.UserPreferences, DEFAULT_PREFERENCES);

  // Merge with defaults to protect against stale localStorage missing new fields
  const preferences = useMemo<UserPreferences>(
    () => ({
      ...DEFAULT_PREFERENCES,
      ...storedPreferences,
      cardVisibility: {
        ...DEFAULT_PREFERENCES.cardVisibility,
        ...storedPreferences?.cardVisibility
      }
    }),
    [storedPreferences]
  );

  // Apply font family to document root whenever it changes. The resolved stack
  // drives --font-family-base, which feeds both CSS text and the Stencil font
  // token (see src/index.tsx), so avatars follow the preference too.
  useEffect(() => {
    document.documentElement.style.setProperty('--font-family-base', resolveFontStack(preferences.fontFamily));
  }, [preferences.fontFamily]);

  const setFontFamily = useCallback(
    (fontFamily: FontFamily) => {
      setPreferences((prev: UserPreferences) => ({ ...prev, fontFamily }));
    },
    [setPreferences]
  );

  const updateCardVisibility = useCallback(
    (card: keyof HomepageCardVisibility, visible: boolean) => {
      setPreferences((prev: UserPreferences) => ({
        ...prev,
        cardVisibility: { ...prev.cardVisibility, [card]: visible }
      }));
    },
    [setPreferences]
  );

  const resetToDefaults = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
  }, [setPreferences]);

  return useMemo(
    () => ({
      preferences,
      setFontFamily,
      updateCardVisibility,
      resetToDefaults
    }),
    [preferences, setFontFamily, updateCardVisibility, resetToDefaults]
  );
};
